/**
 * The client-side shape of the wall.
 *
 * Deliberately declared here rather than inferred from the router, for the same
 * reason as `features/album/types.ts`: the server's row types carry `Date`
 * fields, and what actually crosses the wire is a string. Reusing them compiles
 * and then lies at runtime.
 */

export interface PostAuthor {
  id: string;
  displayName: string;
  avatarFileId: string | null;
}

export interface ReactionTally {
  emoji: string;
  count: number;
}

export interface Post {
  id: string;
  /** Null when the author's account has been removed. */
  author: PostAuthor | null;
  body: string | null;
  folderId: string | null;
  fileIds: string[];
  commentCount: number;
  reactions: ReactionTally[];
  /** Which reactions the signed-in user has set on this post. */
  viewerReactions: string[];
  /**
   * The last two comments, sent with the feed itself.
   *
   * So the wall can show a conversation rather than a count, without a request
   * per card: fetching these per post would be one round trip per visible post,
   * and they arrive from a single window function instead.
   */
  recentComments: Comment[];
  createdAt: string;
}

export interface Comment {
  id: string;
  author: PostAuthor | null;
  body: string;
  createdAt: string;
}

/**
 * The two reactions that get first-class buttons.
 *
 * They are mutually exclusive, which the database's unique index does *not*
 * enforce on its own — the server clears the opposing one when either is set.
 */
export const LIKE = "👍";
export const DISLIKE = "👎";

/**
 * How many photos one post shows.
 *
 * Shared by the composer, the carousel and the URL batch that feeds it — the
 * three disagreeing is how a slide ends up with no image behind it.
 */
export const MAX_POST_PHOTOS = 5;
